package com.eladfinish.bible.contest.screens.browse.usecases

import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.data.Reference
import com.eladfinish.bible.contest.data.common.AssetReader
import com.eladfinish.bible.contest.extensions.convertToDataClass
import com.eladfinish.bible.contest.iyov_tehilim_speakers_json
import io.github.aakira.napier.Napier
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive

class GetIyovTehilimSpeakersUseCase(
    private val assetReader: AssetReader,
) {

    private val parsed: IyovTehilimSpeakers by lazy {
        val content = assetReader.readFile(
            SharedRes.assets.iyov_tehilim_speakers_json,
        )

        content.convertToDataClass<IyovTehilimSpeakers>().also { speakers ->
            // Consider gating logs behind debug flag if this runs often.
            Napier.d("iyovSpeakers: ${speakers.iyovSpeakers.map { it.name }}")
            Napier.d("tehilimAuthors: ${speakers.tehilimAuthors.map { it.name }}")
        }
    }

    fun getIyovSpeakers(): List<NameChapters> = parsed.iyovSpeakers

    fun getTehilimAuthors(): List<NameChapters> = parsed.tehilimAuthors

    companion object {
        fun List<NameChapters>.getVerseSpeaker(
            chapter: Int,
            verse: Int,
            default: String,
        ): String =
            firstOrNull {
                it.chapters.any { chapters ->
                    chapters.contains(chapter, verse)
                }
            }?.name ?: default
    }

}

@Serializable
data class IyovTehilimSpeakers(
    @SerialName("iyovSpeakers") val iyovSpeakers: List<NameChapters>,
    @SerialName("tehilimAuthors") val tehilimAuthors: List<NameChapters>,
) {
    override fun toString(): String =
        "IyovTehilimSpeakers:\niyovSpeakers=$iyovSpeakers\ntehilimAuthors=$tehilimAuthors"
}

@Serializable
data class NameChapters(
    @SerialName("name") val name: String,
    @SerialName("chapters") val rawChapters: List<JsonElement>,
) {
    val chapters: List<Chapters>
        get() = rawChapters.map {
            when (it) {
                is JsonObject -> Chapters.Partial(reference = it.toString().convertToDataClass())
                is JsonPrimitive -> Chapters.Full(number = it.content.toInt())
                else -> error("cannot convert $it")
            }
        }

    override fun toString(): String {
        return "NameChapters(name=$name, chapters=$chapters)"
    }

}

@Serializable
sealed class Chapters {
    @Serializable
    data class Full(val number: Int) : Chapters()

    @Serializable
    data class Partial(val reference: Reference) : Chapters()

    fun contains(chapter: Int, verse: Int): Boolean =
        when (this) {
            is Full -> number == chapter
            is Partial -> reference.chapter == chapter
                    && reference.versesList?.any { verse in (it[0]..it[1]) } == true
            // תיקון שמות דוברים באיוב כאשר באותו פרק יש כמה דוברים שונים
        }
}
